/**
 * MoveHistory Component for Connect Four
 *
 * Displays moves in scrollable panel: format "1. Red → Column 4".
 * Auto-scrolls to latest move.
 *
 * Requirements: 14.1, 14.2, 14.3, 14.4
 */

import React, { useRef, useEffect } from 'react';
import { ConnectFourMove } from '../types';
import './MoveHistory.css';

export interface ConnectFourMoveHistoryProps {
  moves: ConnectFourMove[];
  currentMoveIndex?: number;
}

export const MoveHistory: React.FC<ConnectFourMoveHistoryProps> = ({
  moves,
  currentMoveIndex,
}) => {
  const historyRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest move
  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [moves.length]);

  /**
   * Format a move for display: "Red → Column 4"
   */
  const formatMove = (move: ConnectFourMove): string => {
    const color = move.player === 'red' ? 'Red' : 'Yellow';
    return `${color} → Column ${move.column + 1}`;
  };

  return (
    <div className="cf-move-history">
      <div className="cf-move-history-header">
        <h3>Move History</h3>
      </div>

      <div className="cf-move-history-content" ref={historyRef}>
        {moves.length === 0 ? (
          <div className="cf-move-history-empty">No moves yet</div>
        ) : (
          <div className="cf-move-history-list">
            {moves.map((move, index) => {
              const isActive = currentMoveIndex === index;
              return (
                <div
                  key={index}
                  className={`cf-move-entry ${isActive ? 'cf-move-entry--active' : ''}`}
                >
                  <span className="cf-move-number">{move.moveNumber}.</span>
                  <span className={`cf-move-color cf-move-color--${move.player}`}>
                    {move.player === 'red' ? '🔴' : '🟡'}
                  </span>
                  <span className="cf-move-text">{formatMove(move)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {moves.length > 0 && (
        <div className="cf-move-history-footer">
          <span className="cf-move-count">
            {moves.length} {moves.length === 1 ? 'move' : 'moves'}
          </span>
        </div>
      )}
    </div>
  );
};

export default MoveHistory;
